import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import type { Language } from '@sourcr/shared';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';

interface AppShellProps {
  lang: Language;
  theme: 'dark' | 'light';
  userName: string;
  onLogout: () => void;
  onToggleTheme: () => void;
  onToggleLang: () => void;
}

const PAGE_TITLES: Record<string, string> = {
  '/dashboard': 'Dashboard — Sourcr',
  '/chat': 'New Search — Sourcr',
  '/results': 'Results — Sourcr',
  '/favorites': 'Favorites — Sourcr',
  '/settings': 'Settings — Sourcr',
};

const SIDEBAR_WIDTH = 220;
const MOBILE_BREAKPOINT = 768;

export function AppShell({
  lang,
  theme,
  userName,
  onLogout,
  onToggleTheme,
  onToggleLang,
}: AppShellProps) {
  const location = useLocation();

  // Track viewport width for responsive layout
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < MOBILE_BREAKPOINT);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Update page title on route change
  useEffect(() => {
    const segment = '/' + location.pathname.split('/')[1];
    document.title = PAGE_TITLES[segment] ?? 'Sourcr';
  }, [location.pathname]);

  // Close mobile menu on route change
  useEffect(() => {
    if (isMobile) setMobileMenuOpen(false);
  }, [location.pathname, isMobile]);

  // Resize listener
  useEffect(() => {
    const handler = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      if (!mobile) setMobileMenuOpen(false);
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100dvh',
        backgroundColor: 'var(--bg-base)',
      }}
    >
      {/* Mobile overlay — closes drawer when tapped */}
      {isMobile && mobileMenuOpen && (
        <div
          onClick={() => setMobileMenuOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.45)',
            zIndex: 49,
          }}
          aria-hidden="true"
        />
      )}

      {/* Sidebar — fixed drawer on mobile, static on desktop */}
      <div
        style={
          isMobile
            ? {
                position: 'fixed',
                left: 0,
                top: 0,
                height: '100dvh',
                zIndex: 50,
                transform: mobileMenuOpen ? 'translateX(0)' : 'translateX(-100%)',
                transition: 'transform 0.25s ease',
                willChange: 'transform',
              }
            : undefined
        }
      >
        <Sidebar lang={lang} userName={userName} onLogout={onLogout} />
      </div>

      {/* Main content area — full width on mobile */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          marginLeft: isMobile ? 0 : SIDEBAR_WIDTH,
          minWidth: 0,
        }}
      >
        <TopBar
          lang={lang}
          theme={theme}
          onToggleTheme={onToggleTheme}
          onToggleLang={onToggleLang}
          isMobile={isMobile}
          onOpenMenu={() => setMobileMenuOpen(true)}
        />
        <main
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: isMobile ? 16 : 24,
            backgroundColor: 'var(--bg-base)',
          }}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
